<template>
    <div>
        <!-- Report Table -->
        <q-table
            v-if="selectedGroupBy === 'assets'"
            :rows="report.rows"
            :columns="table.columns"
            :pagination="table.pagination"
            :no-data-label="
                $t('No quality defects were found for this date range.')
            "
            :hide-bottom="report.rows.length > 0"
            :table-class="report.rows.length > 0 ? 'sticky-table' : ''"
            row-key="id"
            :sort-method="rebuildReport"
            :title="label"
            title-class="text-weight-medium text-medium-grey text-body2"
            class="no-shadow border-grey"
        >
            <!-- Header slot -->
            <template v-slot:header="props">
                <q-tr :props="props">
                    <q-th auto-width class="bg-secondary-white" />
                    <q-th
                        v-for="col in props.cols"
                        :key="col.name"
                        :props="props"
                        class="bg-secondary-white"
                    >
                        <span
                            class="text-weight-bold"
                            style="font-size: 14px"
                            >{{ col.label }}</span
                        >
                    </q-th>
                </q-tr>
            </template>

            <!-- Row slot -->
            <template v-slot:body="props">
                <q-tr :props="props">
                    <!-- Expansion button -->
                    <q-td auto-width>
                        <q-btn
                            @click="props.expand = !props.expand"
                            :icon="
                                props.expand
                                    ? 'fas fa-caret-up'
                                    : 'fas fa-caret-down'
                            "
                            color="accent"
                            size="sm"
                            round
                            dense
                            ><q-tooltip>{{
                                props.expand
                                    ? $t('Collapse row')
                                    : $t('Expand row')
                            }}</q-tooltip></q-btn
                        >
                    </q-td>
                    <!-- Fields -->
                    <q-td
                        v-for="col in props.cols"
                        :key="col.name"
                        :props="props"
                    >
                        <div v-if="col.name === 'name'">
                            {{ translateLabel(col.value) }}
                        </div>
                        <div v-else-if="col.name === 'rejected_production'">
                            {{ formatProduction(col.value) }}
                            <q-icon
                                v-if="col.value < 0"
                                name="fas fa-exclamation-circle"
                                color="negative"
                            >
                                <q-tooltip>
                                    {{ this.negativeValueMessage }}
                                </q-tooltip>
                            </q-icon>
                        </div>
                        <div v-else-if="col.name === 'nb_events'">
                            {{ col.value }}
                        </div>
                    </q-td>
                </q-tr>

                <!-- Expanded row -->
                <template v-for="(row, index) in props.row.assets">
                    <q-tr
                        v-if="props.expand"
                        :key="`${row.id}-${row.id}-${index}`"
                    >
                        <q-td auto-width>
                            <div class="q-ml-md">
                                <q-btn
                                    :disable="row.nbEvents == 0"
                                    @click="
                                        $emit('handleShowEvents', {
                                            row,
                                            dateInterval: report.dateInterval
                                        })
                                    "
                                    icon="far fa-eye"
                                    color="accent"
                                    size="sm"
                                    round
                                    dense
                                >
                                    <q-tooltip
                                        >{{ $t('View quality events') }}
                                    </q-tooltip>
                                </q-btn>
                            </div>
                        </q-td>
                        <q-td>
                            <div v-if="getAsset(row['id'])">
                                {{ getAsset(row['id']).name }}
                                <q-badge
                                    v-if="isAssetDeleted(row['id'])"
                                    color="lighter-grey"
                                    text-color="medium-grey"
                                    class="q-ml-sm"
                                >
                                    {{ $t('Deleted') }}
                                </q-badge>
                            </div>
                        </q-td>

                        <q-td>
                            <div class="float-right">
                                {{
                                    formatProduction(row['rejectedProduction'])
                                }}
                                <q-icon
                                    v-if="row['rejectedProduction'] < 0"
                                    name="fas fa-exclamation-circle"
                                    color="negative"
                                >
                                    <q-tooltip>
                                        {{ this.negativeValueMessage }}
                                    </q-tooltip>
                                </q-icon>
                            </div>
                        </q-td>
                        <q-td>
                            <div class="float-right">
                                {{ row['nbEvents'] }}
                            </div>
                        </q-td>
                    </q-tr>
                    <!-- Expanded row -->
                </template>
            </template>

            <!-- Footer slot -->
            <template v-slot:bottom-row>
                <q-tr
                    v-show="report.rows.length > 0"
                    class="text-bold bg-secondary-white"
                >
                    <q-td auto-width></q-td>
                    <q-td>{{ $t('Grand Total') }}</q-td>
                    <q-td align="right">
                        {{ formatProduction(report.totals.rejectedProduction) }}
                        <q-icon
                            v-if="report.totals.rejectedProduction < 0"
                            name="fas fa-exclamation-circle"
                            color="negative"
                        >
                            <q-tooltip>
                                {{ this.negativeValueMessage }}
                            </q-tooltip>
                        </q-icon>
                    </q-td>
                    <q-td align="right">
                        {{ report.totals.nbEvents }}
                    </q-td>
                </q-tr>
            </template>
        </q-table>

        <q-table
            v-if="selectedGroupBy === 'products'"
            :rows="report.rows"
            :columns="table.columns"
            :pagination="table.pagination"
            :no-data-label="
                $t('No quality defects were found for this date range.')
            "
            :hide-bottom="report.rows.length > 0"
            :table-class="report.rows.length > 0 ? 'sticky-table' : ''"
            row-key="id"
            :sort-method="rebuildReport"
            :title="label"
            title-class="text-weight-medium text-medium-grey text-body2"
            class="no-shadow border-grey"
        >
            <!-- Header slot -->
            <template v-slot:header="props">
                <q-tr :props="props">
                    <q-th auto-width class="bg-secondary-white" />
                    <q-th
                        v-for="col in props.cols"
                        :key="col.name"
                        :props="props"
                        class="bg-secondary-white"
                    >
                        <span
                            class="text-weight-bold"
                            style="font-size: 14px"
                            >{{ col.label }}</span
                        >
                    </q-th>
                </q-tr>
            </template>

            <!-- Row slot -->
            <template v-slot:body="props">
                <q-tr :props="props">
                    <!-- Expansion button -->
                    <q-td auto-width>
                        <q-btn
                            @click="props.expand = !props.expand"
                            :icon="
                                props.expand
                                    ? 'fas fa-caret-up'
                                    : 'fas fa-caret-down'
                            "
                            color="accent"
                            size="sm"
                            round
                            dense
                            ><q-tooltip>{{
                                props.expand
                                    ? $t('Collapse row')
                                    : $t('Expand row')
                            }}</q-tooltip></q-btn
                        >
                    </q-td>
                    <!-- Fields -->
                    <q-td
                        v-for="col in props.cols"
                        :key="col.name"
                        :props="props"
                    >
                        <div v-if="col.name === 'name'">
                            {{ translateLabel(col.value) }}
                        </div>
                        <div v-else-if="col.name === 'rejected_production'">
                            {{ formatProduction(col.value) }}
                            <q-icon
                                v-if="col.value < 0"
                                name="fas fa-exclamation-circle"
                                color="negative"
                            >
                                <q-tooltip>
                                    {{ this.negativeValueMessage }}
                                </q-tooltip>
                            </q-icon>
                        </div>
                        <div v-else-if="col.name === 'nb_events'">
                            {{ col.value }}
                        </div>
                    </q-td>
                </q-tr>

                <!-- Expanded row -->
                <template v-for="(row, index) in props.row.products">
                    <q-tr
                        v-if="props.expand"
                        :key="`${row.id}-${row.id}-${index}`"
                    >
                        <q-td auto-width>
                            <div class="q-ml-md">
                                <q-btn
                                    :disable="row.nbEvents == 0"
                                    @click="
                                        $emit('handleShowEvents', {
                                            row,
                                            dateInterval: report.dateInterval
                                        })
                                    "
                                    icon="far fa-eye"
                                    color="accent"
                                    size="sm"
                                    round
                                    dense
                                >
                                    <q-tooltip
                                        >{{ $t('View quality events') }}
                                    </q-tooltip>
                                </q-btn>
                            </div>
                        </q-td>
                        <q-td>
                            <div v-if="row['name']">
                                {{ row['name'] }}
                            </div>
                        </q-td>

                        <q-td>
                            <div class="float-right">
                                {{
                                    formatProduction(row['rejectedProduction'])
                                }}
                                <q-icon
                                    v-if="row['rejectedProduction'] < 0"
                                    name="fas fa-exclamation-circle"
                                    color="negative"
                                >
                                    <q-tooltip>
                                        {{ this.negativeValueMessage }}
                                    </q-tooltip>
                                </q-icon>
                            </div>
                        </q-td>
                        <q-td>
                            <div class="float-right">
                                {{ row['nbEvents'] }}
                            </div>
                        </q-td>
                    </q-tr>
                    <!-- Expanded row -->
                </template>
            </template>

            <!-- Footer slot -->
            <template v-slot:bottom-row>
                <q-tr
                    v-show="report.rows.length > 0"
                    class="text-bold bg-secondary-white"
                >
                    <q-td auto-width></q-td>
                    <q-td>{{ $t('Grand Total') }}</q-td>
                    <q-td align="right">
                        {{ formatProduction(report.totals.rejectedProduction) }}
                        <q-icon
                            v-if="report.totals.rejectedProduction < 0"
                            name="fas fa-exclamation-circle"
                            color="negative"
                        >
                            <q-tooltip>
                                {{ this.negativeValueMessage }}
                            </q-tooltip>
                        </q-icon>
                    </q-td>
                    <q-td align="right">
                        {{ report.totals.nbEvents }}
                    </q-td>
                </q-tr>
            </template>
        </q-table>
    </div>
</template>

<script>
import { formatProductionValue } from '@utils/numberFormatters.js';
import { mapGetters } from 'vuex';
import {
    NON_QUALITY_EVENT_DEFECT_NAME,
    UNJUSTIFIED_DEFECT_NAME
} from './qualityReport';
import { mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'QualityReportTable',
    components: {},
    emits: ['setOrder', 'handleShowEvents'],
    props: {
        report: {
            type: Object,
            required: true
        },
        selectedGroupBy: {
            type: String,
            required: true
        },
        label: {
            type: String,
            required: false
        }
    },
    data() {
        return {
            // negative value message
            negativeValueMessage: this.$t(
                'Negative numbers are often a symptom of misconfiguration. Please verify the sensor configurations or contact support.'
            )
        };
    },
    computed: {
        ...mapGetters({
            getAsset: 'facilities/getAsset'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        table() {
            return {
                columns: [
                    {
                        name: 'name',
                        field: 'name',
                        label: this.$t(this.$t('Quality Defect')),
                        align: 'left'
                    },
                    {
                        name: 'rejected_production',
                        field: 'rejectedProduction',
                        label: this.$t('Number of Rejected Products'),
                        align: 'right',
                        sortable: true
                    },
                    {
                        name: 'nb_events',
                        field: 'nbEvents',
                        label: this.$t('Number of Quality Events'),
                        align: 'right',
                        sortable: true
                    }
                ],
                pagination: {
                    sortBy: 'rejected_production',
                    descending: true,
                    rowsPerPage: 0 // infinite
                }
            };
        }
    },
    methods: {
        formatProductionValue,
        /**
         *  Translate label to current locale (language)
         * @param {String} value - label to translate
         */
        translateLabel(value) {
            const translatableValue = [
                UNJUSTIFIED_DEFECT_NAME,
                NON_QUALITY_EVENT_DEFECT_NAME
            ];
            if (translatableValue.indexOf(value) !== -1) {
                return this.$t(value);
            }
            return value;
        },
        /**
         * Rebuilds the table given a new sort parameter as accumulated
         * downtime needs to be recalculated again
         * @param {Object} rows
         * @param {String} sortBy
         * @param {Boolean} descending
         * @return {Object}
         */
        rebuildReport(rows, sortBy, descending) {
            let orderBy = '';
            for (const col of this.table.columns) {
                if (col.name === sortBy) {
                    orderBy = col.field;
                }
            }

            this.$emit('setOrder', {
                orderBy,
                descending
            });

            return rows;
        },
        /**
         * Verify if the asset is deleted
         * @return {Boolean} true if the asset is deleted
         */
        isAssetDeleted(assetId) {
            const asset = this.getAsset(assetId);
            if (asset && asset.is_deleted) {
                return asset.is_deleted;
            }
            return false;
        },
        /**
         * Format the production value
         * @param {Number} value - The value to format
         * @return {String} The formatted value
         */
        formatProduction(value) {
            return formatProductionValue(value) || '-';
        }
    }
};
</script>

<style lang="scss" scoped>
:deep(.q-table__top) {
    background-color: $secondary-white;
}
</style>
